import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
  Sliders,
  Undo2,
  X,
} from 'lucide-react';
import {
  ETIQUETAS_MOVIMIENTO,
  MOTIVOS_MOVIMIENTO,
  etiquetaMovimiento,
} from '@suminuta/shared';
import PageHeader from '../components/PageHeader';
import ModalRevertirSalida from '../components/ModalRevertirSalida';
import { useKardex } from '../hooks/useMovimientos';
import { useRevertirSalidaBodega } from '../hooks/useIngredientes';
import { useToast } from '../hooks/useToast';
import movimientosService, {
  type FiltrosKardex,
  type MovimientoKardex,
} from '../services/movimientosService';
import type { MovimientoStock } from '../services/ingredientesService';
import {
  ETIQUETAS_PRESET,
  presetDeRango,
  rangoDePreset,
  type PresetRango,
} from '../utils/rangosFecha';
import { formatearFechaCorta } from '../utils/dateUtils';
import BloqueLectura from '../components/reportes/BloqueLectura';

/**
 * Kardex global de movimientos de bodega (PROMPT_N°053).
 *
 * El historial existía sólo por ingrediente, dentro de un modal: no había
 * forma de ver "qué entró y salió hoy" ni de exportarlo, que es lo que
 * hace falta para cruzar la planilla diaria de Juan.
 *
 * Los filtros viven en la query string para poder pasar un link con "lo de
 * hoy" por WhatsApp y que el otro vea exactamente lo mismo.
 */

const TONO: Record<string, string> = {
  compra: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  produccion:
    'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  ajuste: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300',
  merma:
    'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  bodega:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  reverso: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
};

const PRESETS: PresetRango[] = ['hoy', 'ayer', 'semana', 'ultimos7', 'mes'];

const fmtNumero = (n: number) =>
  n.toLocaleString('es-CL', { maximumFractionDigits: 2 });

const fmtHora = (iso: string) =>
  new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));

const Movimientos: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const { success, error: showError } = useToast();
  const revertirMutation = useRevertirSalidaBodega();
  const [exportando, setExportando] = useState(false);
  const [salidaARevertir, setSalidaARevertir] =
    useState<MovimientoKardex | null>(null);

  // Los filtros son la URL: no hay estado duplicado que se pueda
  // desincronizar, y el link es compartible.
  const filtros: FiltrosKardex = useMemo(() => {
    const porDefecto = rangoDePreset('ultimos7');
    const num = (k: string) =>
      params.get(k) ? Number(params.get(k)) : undefined;
    return {
      desde: params.get('desde') ?? porDefecto.desde,
      hasta: params.get('hasta') ?? porDefecto.hasta,
      tipo: (params.get('tipo') as FiltrosKardex['tipo']) ?? undefined,
      motivo: params.get('motivo') ?? undefined,
      ingrediente_id: num('ingrediente_id'),
      usuario_id: num('usuario_id'),
      q: params.get('q') ?? undefined,
      page: num('page') ?? 1,
      limit: num('limit') ?? 50,
    };
  }, [params]);

  const { data, isLoading, error } = useKardex(filtros);

  const presetActivo = presetDeRango({
    desde: filtros.desde!,
    hasta: filtros.hasta!,
  });

  const actualizar = (cambios: Record<string, string | undefined>) => {
    const p = new URLSearchParams(params);
    Object.entries(cambios).forEach(([k, v]) => {
      if (v === undefined || v === '') p.delete(k);
      else p.set(k, v);
    });
    // Cualquier cambio de filtro vuelve a la primera página: quedarse en
    // la 7 de un resultado que ahora tiene 2 muestra una tabla vacía.
    if (!('page' in cambios)) p.delete('page');
    setParams(p, { replace: true });
  };

  const aplicarPreset = (p: PresetRango) => {
    const r = rangoDePreset(p);
    actualizar({ desde: r.desde, hasta: r.hasta });
  };

  const limpiarFiltros = () => {
    const r = rangoDePreset('ultimos7');
    setParams(new URLSearchParams({ desde: r.desde, hasta: r.hasta }), {
      replace: true,
    });
  };

  const hayFiltrosExtra = Boolean(
    filtros.tipo || filtros.motivo || filtros.q || filtros.ingrediente_id
  );

  const handleExportar = async () => {
    setExportando(true);
    try {
      await movimientosService.exportar(filtros);
      success('CSV descargado', 'Se abre en Excel con acentos y columnas.');
    } catch (e: any) {
      // El 413 llega como blob; hay que leerlo para mostrar el mensaje.
      let msg = 'No se pudo exportar. Intentá nuevamente.';
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try {
          msg = JSON.parse(await blob.text()).message ?? msg;
        } catch {
          /* se queda el genérico */
        }
      } else if (e?.response?.data?.message) {
        msg = e.response.data.message;
      }
      showError('No se pudo exportar', msg);
    } finally {
      setExportando(false);
    }
  };

  const handleRevertir = async (motivo: string) => {
    if (!salidaARevertir) return;
    try {
      const r = await revertirMutation.mutateAsync({
        id: salidaARevertir.ingrediente.id,
        movimientoId: salidaARevertir.id,
        motivo,
      });
      setSalidaARevertir(null);
      success(
        'Salida deshecha',
        `Se devolvieron ${r.reverso.cantidad_devuelta} ${r.reverso.unidad}.`
      );
    } catch (e: any) {
      showError(
        'No se pudo deshacer la salida',
        e?.response?.data?.message || 'Intentá nuevamente.'
      );
    }
  };

  const resumen = data?.resumen;

  /** Mismo mecanismo que en los reportes: la frase escribe el filtro. */
  const aplicarFiltroDeFrase = (
    filtro: Record<string, string | number | boolean>
  ) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(filtro)) p.set(k, String(v));
    p.delete('page');
    setParams(p, { replace: true });
    document
      .getElementById('tabla-kardex')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const filas = data?.data ?? [];
  const pag = data?.pagination;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader title="Movimientos de bodega" helpSeccion="movimientos" />

      <div className="px-4 py-6 space-y-6">
        {/* ---------------------------------------------- Filtros */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          {/* Presets */}
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => aplicarPreset(p)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  presetActivo === p
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {ETIQUETAS_PRESET[p]}
              </button>
            ))}
            {presetActivo === 'personalizado' && (
              <span className="px-3 py-1.5 text-sm rounded-full bg-blue-600 text-white">
                {ETIQUETAS_PRESET.personalizado}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Desde
              </label>
              <input
                type="date"
                value={filtros.desde ?? ''}
                onChange={(e) => actualizar({ desde: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Hasta
              </label>
              <input
                type="date"
                value={filtros.hasta ?? ''}
                onChange={(e) => actualizar({ hasta: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Tipo
              </label>
              <select
                value={filtros.tipo ?? ''}
                onChange={(e) => actualizar({ tipo: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Todos</option>
                <option value="entrada">Entradas</option>
                <option value="salida">Salidas</option>
                <option value="ajuste">Ajustes</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Movimiento
              </label>
              <select
                value={filtros.motivo ?? ''}
                onChange={(e) => actualizar({ motivo: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Todos</option>
                {MOTIVOS_MOVIMIENTO.map((m) => (
                  <option key={m} value={m}>
                    {ETIQUETAS_MOVIMIENTO[m].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Ingrediente
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  defaultValue={filtros.q ?? ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')
                      actualizar({ q: (e.target as HTMLInputElement).value });
                  }}
                  onBlur={(e) => actualizar({ q: e.target.value })}
                  placeholder="Buscar por nombre…"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              {hayFiltrosExtra && (
                <button
                  type="button"
                  onClick={limpiarFiltros}
                  className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:underline"
                >
                  <X className="h-4 w-4" />
                  Limpiar filtros
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={handleExportar}
              disabled={exportando || filas.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              {exportando ? 'Generando…' : 'Exportar CSV'}
            </button>
          </div>
        </div>

        <BloqueLectura
          lectura={data?.lectura}
          ayuda="kardex"
          cargando={isLoading}
          onFiltro={aplicarFiltroDeFrase}
        />

        {/* ---------------------------------------------- Resumen */}
        {resumen && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-800 p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Entradas
                </p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {resumen.entradas.n}
                </p>
              </div>
              <ArrowDownCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-800 p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Salidas
                </p>
                <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">
                  {resumen.salidas.n}
                </p>
              </div>
              <ArrowUpCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-violet-200 dark:border-violet-800 p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-violet-700 dark:text-violet-300">
                  Reversos
                </p>
                <p className="text-2xl font-bold text-violet-900 dark:text-violet-100">
                  {resumen.reversos.n}
                </p>
              </div>
              <Undo2 className="h-8 w-8 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  Ajustes
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {resumen.ajustes.n}
                </p>
              </div>
              <Sliders className="h-8 w-8 text-slate-600 dark:text-slate-400" />
            </div>
          </div>
        )}
        {/* Los totales van sólo como conteo: sumar gramos con mililitros y
            unidades daría un número que no significa nada. La cantidad con
            su unidad va por fila.

            Los reversos tienen tarjeta propia (N°054): por su tipo son
            entradas, pero no son mercadería que entró. Sumarlos a Entradas
            hacía parecer que el período tuvo más compras de las que tuvo. */}

        {/* ---------------------------------------------- Tabla */}
        <div
          id="tabla-kardex"
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Cargando movimientos…
              </span>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-700 dark:text-red-300">
              No se pudieron cargar los movimientos.
            </div>
          ) : filas.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-gray-700 dark:text-gray-200 font-medium">
                Sin movimientos entre el{' '}
                {formatearFechaCorta(filtros.desde)} y el{' '}
                {formatearFechaCorta(filtros.hasta)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {hayFiltrosExtra
                  ? 'Probá quitando algún filtro o ampliando el rango.'
                  : 'Probá con un rango más amplio.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    {[
                      'Fecha',
                      'Ingrediente',
                      'Movimiento',
                      'Cantidad',
                      'Stock',
                      'Lote',
                      'Documento',
                      'Usuario',
                      'Detalle',
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filas.map((m) => {
                    const et = etiquetaMovimiento(m.motivo);
                    const positivo = m.tipo === 'entrada';
                    return (
                      <tr
                        key={m.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatearFechaCorta(m.fecha)}
                          <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                            {fmtHora(m.fecha)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {m.ingrediente.nombre}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${TONO[et.tono] ?? TONO.ajuste}`}
                          >
                            <span className="mr-1">{et.emoji}</span>
                            {et.label}
                          </span>
                        </td>
                        <td
                          className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${
                            positivo
                              ? 'text-green-600 dark:text-green-400'
                              : m.tipo === 'salida'
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {m.cantidad > 0 ? '+' : ''}
                          {fmtNumero(m.cantidad)} {m.ingrediente.unidad}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                          {fmtNumero(m.stock_anterior)} →{' '}
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {fmtNumero(m.stock_nuevo)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-gray-600 dark:text-gray-400">
                          {m.lote?.numero_lote ?? '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                          {m.referencia ?? '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {m.usuario.nombre}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 max-w-xs">
                          <div className="truncate" title={m.notas ?? ''}>
                            {m.notas ?? '—'}
                          </div>
                          {m.revertido_por_id && (
                            <span className="mt-1 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                              Revertida
                            </span>
                          )}
                          {m.movimiento_revertido_id && (
                            <span className="text-gray-500 dark:text-gray-500">
                              {' '}
                              (deshace #{m.movimiento_revertido_id})
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {m.motivo === 'salida_bodega' &&
                            !m.revertido_por_id && (
                              <button
                                type="button"
                                title="Deshacer esta salida"
                                onClick={() => setSalidaARevertir(m)}
                                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                              >
                                <Undo2 className="h-3.5 w-3.5" />
                                Deshacer
                              </button>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ------------------------------------------ Paginación */}
          {pag && pag.total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {(pag.page - 1) * pag.limit + 1}–
                {Math.min(pag.page * pag.limit, pag.total)} de{' '}
                <span className="font-medium">{pag.total}</span> movimientos
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => actualizar({ page: String(pag.page - 1) })}
                  disabled={pag.page <= 1}
                  className="p-2 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {pag.page} / {pag.totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => actualizar({ page: String(pag.page + 1) })}
                  disabled={!pag.hasMore}
                  className="p-2 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reutiliza el modal del N°051: la misma acción, desde otra pantalla. */}
      <ModalRevertirSalida
        isOpen={!!salidaARevertir}
        onClose={() => setSalidaARevertir(null)}
        movimiento={
          salidaARevertir
            ? ({
                id: salidaARevertir.id,
                cantidad: salidaARevertir.cantidad,
                unidad_medida: salidaARevertir.ingrediente.unidad,
                lote_id: salidaARevertir.lote?.id ?? null,
                notas: salidaARevertir.notas,
              } as unknown as MovimientoStock)
            : null
        }
        nombreIngrediente={salidaARevertir?.ingrediente.nombre ?? ''}
        onConfirm={handleRevertir}
        isLoading={revertirMutation.isPending}
      />
    </div>
  );
};

export default Movimientos;
